import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ShoppingBag, Plus, Minus, Trash2, Truck, PackageCheck, MapPin, Phone, Mail, Copy, CalendarDays, ShieldCheck, FileText, ChevronLeft, ChevronRight, X } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const HERO = "https://images.pexels.com/photos/29040917/pexels-photo-29040917.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";

function TermsDialog({ open, setOpen, site, items }) {
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#F9F8F6] max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl font-light">Rental Agreement</DialogTitle>
          <DialogDescription>Please read our terms, damage/loss policy, and replacement-cost schedule.</DialogDescription>
        </DialogHeader>
        <div className="text-sm text-[#1A1514]/80 whitespace-pre-wrap leading-relaxed" data-testid="terms-text">{site.terms}</div>
        {items?.length > 0 && (
          <div className="mt-4">
            <p className="text-xs uppercase tracking-[0.2em] text-[#1A1514]/50 mb-2">Replacement Cost Schedule (US)</p>
            <div className="bg-white rounded-xl border border-[#1A1514]/10 divide-y divide-[#1A1514]/5">
              {items.map((i) => (
                <div key={i.id} className="flex justify-between px-4 py-2 font-mono text-sm">
                  <span>{i.name}</span><span>${(i.replacement_cost || 0).toFixed(2)} / {i.unit}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Header({ onCart, count, site }) {
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#F9F8F6]/85 border-b border-[#1A1514]/10">
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <a href="/" className="flex items-center gap-3" data-testid="brand-logo">
          {site.logo_url
            ? <img src={site.logo_url} alt={site.business_name} className="h-11 w-auto object-contain" />
            : <span className="font-heading text-2xl">{site.business_name}</span>}
        </a>
        <nav className="hidden md:flex items-center gap-8 text-sm">
          <a href="#collection" className="hover:text-[#D4AF37] transition-colors">Collection</a>
          <a href="#delivery" className="hover:text-[#D4AF37] transition-colors">Delivery & Pickup</a>
          <a href="#contact" className="hover:text-[#D4AF37] transition-colors">Contact</a>
          <a href="/admin/login" className="text-[#1A1514]/50 hover:text-[#1A1514] transition-colors">Admin</a>
        </nav>
        <Button onClick={onCart} data-testid="open-cart-btn"
          className="rounded-full bg-[#1A1514] text-[#F9F8F6] hover:bg-[#1A1514]/90 relative">
          <ShoppingBag className="w-4 h-4 mr-2" /> Cart
          {count > 0 && <span data-testid="cart-count"
            className="absolute -top-2 -right-2 bg-[#D4AF37] text-[#1A1514] text-xs w-5 h-5 rounded-full flex items-center justify-center font-semibold">{count}</span>}
        </Button>
      </div>
    </header>
  );
}

function Hero({ site }) {
  return (
    <section className="relative h-[78vh] min-h-[520px] flex items-end overflow-hidden">
      <img src={HERO} alt="Elegant event setup" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/40" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}
        className="relative max-w-7xl mx-auto px-6 pb-20 text-[#F9F8F6]">
        <p className="text-xs uppercase tracking-[0.3em] mb-4 text-[#D4AF37]">{site.business_name} · Randallstown, MD</p>
        <h1 className="font-heading font-light text-5xl md:text-7xl leading-none max-w-3xl">
          {site.tagline || "Rentals that make your event unforgettable."}
        </h1>
        <p className="mt-6 max-w-xl text-lg font-light text-white/85">{site.about}</p>
        <a href="#collection">
          <Button className="mt-8 rounded-full bg-[#D4AF37] text-[#1A1514] hover:bg-[#D4AF37]/90 px-8 py-6 text-base" data-testid="browse-btn">
            Browse the Collection
          </Button>
        </a>
      </motion.div>
    </section>
  );
}

function Lightbox({ open, setOpen, gallery, index, setIndex, name }) {
  if (!open) return null;
  const go = (d) => setIndex((index + d + gallery.length) % gallery.length);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#1A1514] border-0 max-w-3xl p-0 overflow-hidden" data-testid="lightbox">
        <DialogHeader className="sr-only"><DialogTitle>{name} photos</DialogTitle>
          <DialogDescription>Browse photos of {name}</DialogDescription></DialogHeader>
        <div className="relative">
          <img src={mediaUrl(gallery[index])} alt={`${name} ${index + 1}`} className="w-full max-h-[75vh] object-contain bg-[#1A1514]" />
          {gallery.length > 1 && <>
            <button onClick={() => go(-1)} data-testid="lightbox-prev"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"><ChevronLeft className="w-5 h-5" /></button>
            <button onClick={() => go(1)} data-testid="lightbox-next"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"><ChevronRight className="w-5 h-5" /></button>
          </>}
          <button onClick={() => setOpen(false)} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"><X className="w-4 h-4" /></button>
        </div>
        {gallery.length > 1 && (
          <div className="flex gap-2 p-3 overflow-x-auto bg-[#1A1514]">
            {gallery.map((g, i) => (
              <button key={i} onClick={() => setIndex(i)}
                className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${i === index ? "border-[#D4AF37]" : "border-transparent opacity-60"}`}>
                <img src={mediaUrl(g)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ProductCard({ item, onAdd, index, dated }) {
  const [qty, setQty] = useState(1);
  const gallery = ((item.images && item.images.length ? item.images : [item.image]) || []).filter(Boolean);
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const low = item.available_quantity < 20;
  const out = item.available_quantity <= 0;
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.08 }}
      className="group bg-white border border-[#1A1514]/10 rounded-xl overflow-hidden hover-lift" data-testid={`product-card-${item.id}`}>
      <div className="relative h-56 overflow-hidden cursor-zoom-in" onClick={() => gallery.length > 0 && setLightbox(true)} data-testid={`product-image-${item.id}`}>
        <img src={mediaUrl(gallery[active] || item.image)} alt={item.name} className="w-full h-full object-cover img-zoom" />
        <span className={`absolute top-3 left-3 text-xs px-3 py-1 rounded-full font-medium ${out ? "bg-[#8B2E2E] text-white" : low ? "bg-[#B56D22] text-white" : "bg-[#2E4F3B] text-white"}`}>
          {out ? "Fully booked" : `${item.available_quantity} available${dated ? " that day" : ""}`}
        </span>
        {gallery.length > 1 && <span className="absolute bottom-3 right-3 text-[11px] px-2 py-0.5 rounded-full bg-black/55 text-white">{gallery.length} photos</span>}
      </div>
      {gallery.length > 1 && (
        <div className="flex gap-2 px-5 pt-3">
          {gallery.slice(0, 5).map((g, i) => (
            <button key={i} onClick={() => setActive(i)} data-testid={`thumb-${item.id}-${i}`}
              className={`w-12 h-12 rounded-md overflow-hidden border-2 transition-all ${i === active ? "border-[#D4AF37]" : "border-[#1A1514]/10 opacity-70 hover:opacity-100"}`}>
              <img src={mediaUrl(g)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
      <div className="p-5">
        <p className="text-xs uppercase tracking-[0.2em] text-[#1A1514]/50">{item.category}</p>
        <h3 className="font-heading text-2xl mt-1">{item.name}</h3>
        <p className="text-sm text-[#1A1514]/60 mt-1 line-clamp-2 h-10">{item.description}</p>
        <div className="flex items-center justify-between mt-4">
          <span className="font-mono text-lg">${item.price.toFixed(2)}<span className="text-xs text-[#1A1514]/50">/{item.unit}</span></span>
          <div className="flex items-center border border-[#1A1514]/15 rounded-full">
            <button className="p-2" onClick={() => setQty(Math.max(1, qty - 1))} data-testid={`qty-minus-${item.id}`}><Minus className="w-3 h-3" /></button>
            <span className="w-8 text-center text-sm font-mono" data-testid={`qty-val-${item.id}`}>{qty}</span>
            <button className="p-2" onClick={() => setQty(Math.min(item.available_quantity, qty + 1))} data-testid={`qty-plus-${item.id}`}><Plus className="w-3 h-3" /></button>
          </div>
        </div>
        {item.replacement_cost > 0 && (
          <p className="text-[11px] text-[#1A1514]/40 mt-2 flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Replacement if damaged/lost: ${item.replacement_cost.toFixed(2)}
          </p>
        )}
        <Button disabled={out} onClick={() => { onAdd(item, qty); }} data-testid={`add-to-cart-${item.id}`}
          className="w-full mt-3 rounded-full bg-[#1A1514] text-[#F9F8F6] hover:bg-[#1A1514]/90">
          Add to Reservation
        </Button>
      </div>
      <Lightbox open={lightbox} setOpen={setLightbox} gallery={gallery} index={active} setIndex={setActive} name={item.name} />
    </motion.div>
  );
}

function ZelleDialog({ info, onClose }) {
  if (!info) return null;
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };
  return (
    <Dialog open={!!info} onOpenChange={onClose}>
      <DialogContent className="bg-[#F9F8F6]">
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl font-light">Complete your Zelle deposit</DialogTitle>
          <DialogDescription>Your reservation is held. Send the 10% deposit via Zelle to confirm it. The remaining balance is due at pickup/delivery.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2" data-testid="zelle-details">
          <div className="bg-white rounded-xl border border-[#1A1514]/10 p-4 space-y-2 font-mono text-sm">
            <div className="flex justify-between items-center"><span className="text-[#1A1514]/50">Deposit due now (10%)</span><span data-testid="zelle-deposit">${Number(info.deposit).toFixed(2)}</span></div>
            <div className="flex justify-between items-center"><span className="text-[#1A1514]/50">Balance at pickup/delivery</span><span data-testid="zelle-balance">${Number(info.balance_due).toFixed(2)}</span></div>
            <div className="flex justify-between items-center"><span className="text-[#1A1514]/50">Zelle name</span>
              <button className="flex items-center gap-2" onClick={() => copy(info.zelle_name)}>{info.zelle_name}<Copy className="w-3 h-3" /></button></div>
            <div className="flex justify-between items-center"><span className="text-[#1A1514]/50">Zelle number</span>
              <button className="flex items-center gap-2" onClick={() => copy(info.zelle_number)}>{info.zelle_number}<Copy className="w-3 h-3" /></button></div>
          </div>
          <p className="text-xs text-[#1A1514]/50">Please include your name in the Zelle memo. We'll confirm your reservation once payment is received.</p>
          <Button onClick={onClose} className="w-full rounded-full bg-[#1A1514] text-[#F9F8F6]" data-testid="zelle-done-btn">Done</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CartSheet({ open, setOpen, site, items }) {
  const cart = useCart();
  const [config, setConfig] = useState({ pickup_address: "", tax_rate: 0.06, delivery_service_fee: 75, mile_rate: 2, mile_multiplier: 4, deposit_rate: 0.10 });
  const [delivery, setDelivery] = useState(false);
  const [pickup, setPickup] = useState(false);
  const [address, setAddress] = useState("");
  const [miles, setMiles] = useState("");
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [zelle, setZelle] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  useEffect(() => { api.get("/config").then((r) => setConfig(r.data)).catch(() => {}); }, []);

  const milesNum = parseFloat(miles) || 0;
  const fee = delivery ? config.delivery_service_fee + milesNum * config.mile_rate * config.mile_multiplier : 0;
  const tax = cart.subtotal * config.tax_rate;
  const total = cart.subtotal + tax + fee;
  const deposit = total * config.deposit_rate;
  const balanceDue = total - deposit;

  const checkout = async (method) => {
    if (cart.items.length === 0) return toast.error("Your reservation is empty.");
    if (!cart.eventDate) return toast.error("Please choose your event date.");
    if (!form.customer_name || !form.customer_email || !form.customer_phone)
      return toast.error("Please fill in your contact details.");
    if (delivery && (!address || milesNum <= 0)) return toast.error("For delivery, enter your venue address and distance in miles.");
    if (!agreed) return toast.error("Please accept the rental agreement to continue.");
    setLoading(true);
    try {
      const r = await api.post("/checkout", {
        ...form, event_date: cart.eventDate, method, origin_url: window.location.origin, agreed_to_terms: agreed,
        lines: cart.items.map((l) => ({ item_id: l.item_id, quantity: l.quantity })),
        fulfillment: { delivery, pickup, address, miles: milesNum },
      });
      if (r.data.provider === "zelle") { setZelle(r.data); cart.clear(); setLoading(false); return; }
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Checkout failed."); setLoading(false); }
  };

  return (
    <>
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto bg-[#F9F8F6]">
        <SheetHeader><SheetTitle className="font-heading text-3xl font-light">Your Reservation</SheetTitle>
          <SheetDescription className="sr-only">Review your rental items, choose delivery or pickup, enter your details and check out.</SheetDescription></SheetHeader>
        <div className="mt-6 space-y-4">
          {cart.items.length === 0 && <p className="text-[#1A1514]/50 text-sm">No items yet. Add pieces from the collection.</p>}
          {cart.items.map((l) => (
            <div key={l.item_id} className="flex gap-3 items-center border-b border-[#1A1514]/10 pb-4" data-testid={`cart-line-${l.item_id}`}>
              <img src={mediaUrl(l.image)} alt={l.name} className="w-16 h-16 rounded-lg object-cover" />
              <div className="flex-1">
                <p className="font-medium text-sm">{l.name}</p>
                <p className="font-mono text-xs text-[#1A1514]/60">${l.price.toFixed(2)}/{l.unit}</p>
                <div className="flex items-center gap-2 mt-1">
                  <button onClick={() => cart.setQty(l.item_id, l.quantity - 1)}><Minus className="w-3 h-3" /></button>
                  <span className="font-mono text-sm w-6 text-center">{l.quantity}</span>
                  <button onClick={() => cart.setQty(l.item_id, l.quantity + 1)}><Plus className="w-3 h-3" /></button>
                </div>
              </div>
              <button onClick={() => cart.remove(l.item_id)} data-testid={`remove-${l.item_id}`}><Trash2 className="w-4 h-4 text-[#8B2E2E]" /></button>
            </div>
          ))}

          {cart.items.length > 0 && <>
            <div className="bg-white rounded-xl p-4 border border-[#1A1514]/10">
              <Label className="text-xs flex items-center gap-2"><CalendarDays className="w-4 h-4" /> Event date</Label>
              <Input type="date" value={cart.eventDate} onChange={(e) => cart.setEventDate(e.target.value)} data-testid="cart-event-date" />
            </div>

            <div id="delivery" className="bg-white rounded-xl p-4 border border-[#1A1514]/10 space-y-3">
              <p className="text-xs uppercase tracking-[0.2em] text-[#1A1514]/50">Delivery & Pickup (optional)</p>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm"><Truck className="w-4 h-4" /> Delivery to my venue</span>
                <Switch checked={delivery} onCheckedChange={setDelivery} data-testid="delivery-toggle" />
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-2 text-sm"><PackageCheck className="w-4 h-4" /> Pickup after event</span>
                <Switch checked={pickup} onCheckedChange={setPickup} data-testid="pickup-toggle" />
              </div>
              {!pickup && (
                <div className="text-xs bg-[#F4EFE6] border border-[#D4AF37]/40 rounded-lg p-3" data-testid="return-notice">
                  <p className="font-medium text-[#1A1514]">Return to pickup location</p>
                  <p className="text-[#1A1514]/70 mt-1">You'll return items to {config.pickup_address} by 10:00am the day after your event. Late returns are automatically billed one extra day.</p>
                </div>
              )}
              {delivery && <>
                <Input placeholder="Delivery address (from 3606 Blair Ave, Randallstown MD 21133)" value={address} onChange={(e) => setAddress(e.target.value)} data-testid="address-input" />
                <div>
                  <Label className="text-xs">Distance from our pickup location (miles, one-way)</Label>
                  <Input type="number" min="0" step="0.1" placeholder="e.g. 20" value={miles}
                    onChange={(e) => setMiles(e.target.value)} data-testid="miles-input" />
                </div>
                <div className="text-xs bg-[#F4EFE6] border border-[#D4AF37]/40 rounded-lg p-3" data-testid="delivery-fee-notice">
                  Delivery = ${(config.delivery_service_fee ?? 75).toFixed(2)} service fee + ${(config.mile_rate ?? 2).toFixed(2)}/mile × {config.mile_multiplier ?? 4} (round trips for drop-off &amp; collection). Please enter your distance honestly; we may confirm it.
                </div>
              </>}
              <p className="text-[11px] text-[#1A1514]/40">Pickup at {config.pickup_address}. Leave both off to arrange your own transport.</p>
            </div>

            <div className="bg-white rounded-xl p-4 border border-[#1A1514]/10 space-y-3">
              <Input placeholder="Full name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="cust-name" />
              <Input type="email" placeholder="Email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} data-testid="cust-email" />
              <Input placeholder="Phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} data-testid="cust-phone" />
              <Textarea placeholder="Notes (colors, setup time, etc.)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cust-notes" />
            </div>

            <div className="bg-white rounded-xl p-4 border border-[#1A1514]/10 font-mono text-sm space-y-1">
              <div className="flex justify-between"><span>Subtotal</span><span data-testid="summary-subtotal">${cart.subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between"><span>Sales tax (6%)</span><span data-testid="summary-tax">${tax.toFixed(2)}</span></div>
              {delivery && <div className="flex justify-between"><span>Delivery</span><span data-testid="summary-fee">${fee.toFixed(2)}</span></div>}
              <div className="flex justify-between text-lg pt-2 border-t border-[#1A1514]/10"><span>Total</span><span data-testid="summary-total">${total.toFixed(2)}</span></div>
              <div className="flex justify-between text-[#2E4F3B] pt-2 border-t border-[#1A1514]/10"><span>Deposit due now (10%)</span><span data-testid="summary-deposit">${deposit.toFixed(2)}</span></div>
              <div className="flex justify-between text-[#1A1514]/60"><span>Balance at pickup/delivery</span><span data-testid="summary-balance">${balanceDue.toFixed(2)}</span></div>
            </div>

            <div className="flex items-start gap-3 bg-white rounded-xl p-4 border border-[#1A1514]/10">
              <Checkbox id="agree" checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} data-testid="agree-terms" className="mt-1" />
              <label htmlFor="agree" className="text-xs text-[#1A1514]/70 leading-relaxed">
                I have read and agree to the{" "}
                <button type="button" onClick={() => setShowTerms(true)} className="text-[#D4AF37] underline" data-testid="open-terms-btn">Rental Agreement</button>,
                including the damage/loss replacement-cost policy, and authorize charges to my card for late returns, damage, or loss.
              </label>
            </div>

            <Button onClick={() => checkout("stripe")} disabled={loading} data-testid="pay-card-btn"
              className="w-full rounded-full bg-[#1A1514] text-[#F9F8F6] hover:bg-[#1A1514]/90 py-6">
              {loading ? "Redirecting..." : "Pay by Card (Stripe)"}
            </Button>
            <div className="grid grid-cols-2 gap-3">
              <Button onClick={() => checkout("paypal")} disabled={loading} variant="outline" data-testid="pay-paypal-btn"
                className="rounded-full border-[#1A1514]/20 py-6">PayPal</Button>
              <Button onClick={() => checkout("zelle")} disabled={loading} variant="outline" data-testid="pay-zelle-btn"
                className="rounded-full border-[#D4AF37] text-[#1A1514] py-6">Zelle</Button>
            </div>
          </>}
        </div>
      </SheetContent>
    </Sheet>
    <ZelleDialog info={zelle} onClose={() => setZelle(null)} />
    <TermsDialog open={showTerms} setOpen={setShowTerms} site={site} items={items} />
    </>
  );
}

function InquirySection({ site }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const submit = async () => {
    if (!f.name || !f.email || !f.message) return toast.error("Please fill name, email and message.");
    try { await api.post("/inquiries", f); setSent(true); toast.success("Inquiry sent! We'll be in touch shortly."); }
    catch { toast.error("Could not send inquiry."); }
  };
  return (
    <section id="contact" className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-16">
      <div>
        <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Get in touch</p>
        <h2 className="font-heading text-4xl md:text-5xl font-light mt-3">Questions about your event?</h2>
        <p className="text-[#1A1514]/60 mt-4 max-w-md">Send us an inquiry and our team will help you plan the perfect setup — availability, custom quantities, and logistics.</p>
        <div className="mt-8 space-y-3 text-sm">
          <p className="flex items-center gap-3"><MapPin className="w-4 h-4 text-[#D4AF37]" /> {site.address}</p>
          <p className="flex items-center gap-3"><Phone className="w-4 h-4 text-[#D4AF37]" /> {site.phone}{site.phone2 ? ` · ${site.phone2}` : ""}</p>
          <p className="flex items-center gap-3"><Mail className="w-4 h-4 text-[#D4AF37]" /> {site.email}</p>
        </div>
        <div className="mt-6 bg-white rounded-xl border border-[#1A1514]/10 p-4 text-sm">
          <p className="text-xs uppercase tracking-[0.2em] text-[#1A1514]/50 mb-2">Pay by Zelle</p>
          <p className="font-medium">{site.zelle_name}</p>
          <p className="font-mono text-[#1A1514]/70">{site.zelle_number}</p>
        </div>
        {(site.custom_info || []).length > 0 && <div className="mt-6 space-y-2 text-sm">
          {site.custom_info.map((c, i) => <p key={i}><span className="text-[#1A1514]/50">{c.label}: </span>{c.value}</p>)}
        </div>}
      </div>
      <div className="bg-white rounded-xl p-8 border border-[#1A1514]/10 space-y-4">
        {sent ? <p className="text-[#2E4F3B]" data-testid="inquiry-success">Thank you! Your inquiry has been received.</p> : <>
          <Input placeholder="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="inq-name" />
          <Input type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="inq-email" />
          <Input placeholder="Phone (optional)" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="inq-phone" />
          <Textarea placeholder="How can we help?" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="inq-message" />
          <Button onClick={submit} data-testid="inq-submit" className="rounded-full bg-[#1A1514] text-[#F9F8F6] hover:bg-[#1A1514]/90 px-8">Send Inquiry</Button>
        </>}
      </div>
    </section>
  );
}

export default function Storefront() {
  const [items, setItems] = useState([]);
  const [site, setSite] = useState({ business_name: "Pascy Lux Event Rentals", custom_info: [] });
  const [open, setOpen] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const cart = useCart();

  useEffect(() => { api.get("/site").then((r) => setSite(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const url = cart.eventDate ? `/availability?date=${cart.eventDate}` : "/items";
    api.get(url).then((r) => setItems(r.data)).catch(() => {});
  }, [cart.eventDate]);

  const onAdd = (item, qty) => { cart.add(item, qty); toast.success(`${qty} × ${item.name} added.`); setOpen(true); };

  return (
    <div>
      <Header onCart={() => setOpen(true)} count={cart.count} site={site} />
      <Hero site={site} />
      <section id="collection" className="max-w-7xl mx-auto px-6 py-24">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">The Collection</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light mt-3">Reserve premium pieces</h2>
          </div>
          <div className="bg-white rounded-xl border border-[#1A1514]/10 p-4 w-full sm:w-auto">
            <Label className="text-xs flex items-center gap-2 text-[#1A1514]/60"><CalendarDays className="w-4 h-4" /> Check availability for your event date</Label>
            <Input type="date" value={cart.eventDate} onChange={(e) => cart.setEventDate(e.target.value)} data-testid="event-date-selector" className="mt-1" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-8">
          {items.map((it, i) => <ProductCard key={it.id} item={it} onAdd={onAdd} index={i} dated={!!cart.eventDate} />)}
        </div>
      </section>
      <InquirySection site={site} />
      <footer className="border-t border-[#1A1514]/10 py-10 text-center text-sm text-[#1A1514]/50">
        {site.logo_url && <img src={site.logo_url} alt={site.business_name} className="h-14 mx-auto mb-3 object-contain" />}
        <p className="font-heading text-2xl text-[#1A1514] mb-2">{site.business_name}</p>
        <p>{site.address} · {site.phone} · © 2026</p>
        <button onClick={() => setShowTerms(true)} className="mt-3 inline-flex items-center gap-1 text-[#D4AF37] underline" data-testid="footer-terms-btn">
          <FileText className="w-3 h-3" /> Rental Agreement & Policies
        </button>
      </footer>
      <CartSheet open={open} setOpen={setOpen} site={site} items={items} />
      <TermsDialog open={showTerms} setOpen={setShowTerms} site={site} items={items} />
    </div>
  );
}
